import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Snackbar, Typography } from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { GridLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import {
  buscarSumarizacaoContribuicoesApuracao,
  exportarSumarizacaoApuracao,
} from "../../../services/apuracao.service";
import Pagination from "../../../components/Pagination";
import BotaoExportacaoProgresso from "../../../components/BotaoExportacaoProgresso";
import useExportacaoAssincrona from "../Detalhe/hooks/useExportacaoAssincrona";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import SelectFiltro, { BuscaFiltro } from "../components/SelectFiltro";
import BadgeStatus from "../components/BadgeStatus";
import {
  usePermissoes,
  CHAVES_APURACOES_EXPORT,
} from "../../../config/Context/PermissoesContext";
import { sumarizacaoEmAndamento, formatarCnpj } from "../utils";
import { formatarMoedaBRL } from "../Detalhe/utils/calcularTotaisPorAno.util";
import { formatarQuantidadeLancamentos } from "../Detalhe/utils/formatarQuantidade.util";
import styles from "../Detalhe/detalhe.styles.module.css";
import tableStyles from "../../../components/TableCard/TableView/styles.module.css";

const NUM_TH = { textAlign: "right" };
const NUM_TD = { textAlign: "right", fontFamily: "ui-monospace, monospace" };

/**
 * TXM-600 — o card não lista Competência entre as colunas/filtros da aba (só CNPJ · Bloco
 * ou registro/descrição (busca) · Validação) — o mesmo grão do protótipo
 * (apuracao_sped_contribuicoes_detalhes.html): 1 linha por bloco/registro, somando os
 * lançamentos de TODAS as competências da apuração. A tabela relatorios.sumarizacao_
 * contribuicoesapuracao continua granular por competência (útil pra série histórica/
 * drill-down), então a agregação é feita aqui na tela, não no banco.
 *
 * Descrição é uma string fixa por bloco (ver preencher_contribuicoesapuracao — nunca varia
 * por competência), então usar a do primeiro registro é seguro. Débito/crédito somam só os
 * valores não nulos, preservando "—" quando NENHUMA competência tem valor (F100/C170).
 *
 * Validação do agregado segue prioridade Revisar > Pendente > OK (pior caso vence, pra não
 * esconder uma competência com problema atrás de outras corretas): "Revisar" significa que
 * ALGUMA competência foi conferida e não bateu; "Pendente" significa que nenhuma bateu mal,
 * mas pelo menos uma não tem regra de validação conhecida (F100/C170, sempre Pendente — ver
 * preencher_contribuicoesapuracao); só é "OK" se TODAS as competências desse bloco forem OK.
 */
function agregarPorBloco(linhas) {
  const porBloco = new Map();
  for (const linha of linhas) {
    const chave = linha.blocoRegistro;
    if (!porBloco.has(chave)) {
      porBloco.set(chave, {
        blocoRegistro: linha.blocoRegistro,
        descricao: linha.descricao,
        qtdLancamentos: 0,
        debito: null,
        credito: null,
        temRevisar: false,
        temPendente: false,
      });
    }
    const acc = porBloco.get(chave);
    acc.qtdLancamentos += Number(linha.qtdLancamentos) || 0;
    if (linha.debito != null) acc.debito = (acc.debito || 0) + Number(linha.debito);
    if (linha.credito != null) acc.credito = (acc.credito || 0) + Number(linha.credito);
    if (linha.validacao === "Revisar") acc.temRevisar = true;
    else if (linha.validacao === "Pendente") acc.temPendente = true;
  }
  return Array.from(porBloco.values()).map((acc) => ({
    ...acc,
    saldo: acc.debito == null && acc.credito == null ? null : (acc.debito || 0) - (acc.credito || 0),
    validacao: acc.temRevisar ? "Revisar" : acc.temPendente ? "Pendente" : "OK",
  }));
}

/**
 * TXM-600 — KPIs do card: totais de débito/crédito somam todas as linhas do filtro (cada
 * bloco/registro — M210, M200, M610, M600, F100, C170 — é uma linha), Saldo apurado é a
 * diferença entre os dois totais, e Registros validados é a fração de linhas com
 * Validação=OK sobre o total de linhas no filtro (F100/C170 sempre contam como "Revisar" —
 * ver preencher_contribuicoesapuracao).
 */
function calcularTotais(linhas) {
  const totais = linhas.reduce(
    (acc, linha) => {
      acc.totalDebitos += Number(linha.debito) || 0;
      acc.totalCreditos += Number(linha.credito) || 0;
      if (linha.validacao === "OK") acc.qtdValidados += 1;
      return acc;
    },
    { totalDebitos: 0, totalCreditos: 0, qtdValidados: 0 }
  );
  return {
    totalDebitos: totais.totalDebitos,
    totalCreditos: totais.totalCreditos,
    saldoApurado: totais.totalDebitos - totais.totalCreditos,
    totalRegistrosValidados: totais.qtdValidados,
    totalRegistros: linhas.length,
  };
}

export default function AbaContribuicoes({ apuracaoId, onTotaisChange, bare }) {
  const { pode } = usePermissoes();
  const podeExportar = pode(CHAVES_APURACOES_EXPORT);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [linhas, setLinhas] = useState([]);
  const [cnpj, setCnpj] = useState("");
  const [infoSnack, setInfoSnack] = useState({ open: false, msg: "", severity: "info" });
  const [cnpjFiltro, setCnpjFiltro] = useState("");
  const [busca, setBusca] = useState("");
  const [validacaoFiltro, setValidacaoFiltro] = useState("");
  const [linhasPagina, setLinhasPagina] = useState([]);

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setLoading(true);
      setErro("");
      const res = await buscarSumarizacaoContribuicoesApuracao(apuracaoId);
      if (!ativo) return;
      if (!res.sucesso) {
        setErro(res.mensagem || "Sumarização indisponível para esta apuração.");
        setLoading(false);
        return;
      }
      setLinhas(res.linhas);
      setCnpj(res.apuracao?.cnpj || "");
      setLoading(false);
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [apuracaoId]);

  /** TXM-600 — o card lista CNPJ como primeira coluna/filtro. A apuração ainda é de 1 CNPJ
   * (multi-CNPJ é o TXM-601), então hoje o select tem uma opção só. */
  const cnpjOpcoes = useMemo(
    () => [
      { value: "", label: "Todos" },
      ...(cnpj ? [{ value: cnpj, label: formatarCnpj(cnpj) }] : []),
    ],
    [cnpj]
  );

  const validacaoOpcoes = [
    { value: "", label: "Todas" },
    { value: "OK", label: "OK" },
    { value: "Revisar", label: "Revisar" },
    { value: "Pendente", label: "Pendente" },
  ];

  const linhasAgregadas = useMemo(() => agregarPorBloco(linhas), [linhas]);

  const linhasFiltradas = useMemo(() => {
    let base = linhasAgregadas;
    if (cnpjFiltro && cnpjFiltro !== cnpj) base = [];
    const q = busca.trim().toLowerCase();
    if (q) {
      base = base.filter(
        (linha) =>
          linha.blocoRegistro.toLowerCase().includes(q) ||
          (linha.descricao || "").toLowerCase().includes(q)
      );
    }
    if (validacaoFiltro) base = base.filter((linha) => linha.validacao === validacaoFiltro);
    return [...base].sort((a, b) => a.blocoRegistro.localeCompare(b.blocoRegistro));
  }, [linhasAgregadas, cnpj, cnpjFiltro, busca, validacaoFiltro]);

  const totais = useMemo(() => calcularTotais(linhasFiltradas), [linhasFiltradas]);

  useEffect(() => {
    onTotaisChange?.(totais);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totais]);

  function limparFiltros() {
    setCnpjFiltro("");
    setBusca("");
    setValidacaoFiltro("");
  }

  // Enfileira e acompanha o job até o download (antes só enfileirava e o arquivo nunca
  // chegava ao usuário) — ver Detalhe/hooks/useExportacaoAssincrona.js.
  const exportacao = useExportacaoAssincrona({
    apuracaoId,
    chave: "agrupado",
    enfileirar: () =>
      exportarSumarizacaoApuracao(apuracaoId, { tipo: "agrupado", formato: "csv" }),
    onFinalizar: ({ severity, msg }) => setInfoSnack({ open: true, msg, severity }),
  });

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <GridLoader color="#1565c0" size={12} />
      </Box>
    );
  }

  if (erro) {
    const emAndamento = sumarizacaoEmAndamento(erro);
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity={emAndamento ? "info" : "error"}>
          {emAndamento
            ? "Sumarização em processamento — os dados aparecerão aqui assim que for concluída."
            : erro}
        </Alert>
      </Box>
    );
  }

  return (
    <>
    <ApuracaoCardShell
      bare={bare}
      conteudoComScrollProprio
      Icone={ReceiptLongIcon}
      titulo="Blocos e registros apurados (SPED Contribuições)"
      tituloSx={{ fontSize: "1.25rem" }}
      acaoHeader={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ color: "#666", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
            {linhasFiltradas.length} registros no filtro
          </Typography>
          <BotaoExportacaoProgresso
            progresso={exportacao}
            rotulo="Exportar CSV"
            variant="contained"
            size="small"
            disabled={!podeExportar}
            onClick={() => exportacao.iniciar()}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#18316e", minHeight: 40, whiteSpace: "nowrap", px: 2, "&:hover": { bgcolor: "#152858" } }}
          />
        </Box>
      }
      filtros={
        <>
          <SelectFiltro label="CNPJ" value={cnpjFiltro} onChange={setCnpjFiltro} options={cnpjOpcoes} />
          <BuscaFiltro
            label="Bloco / registro ou descrição"
            placeholder="Ex.: M200"
            value={busca}
            onChange={setBusca}
          />
          <SelectFiltro label="Validação" value={validacaoFiltro} onChange={setValidacaoFiltro} options={validacaoOpcoes} />
          <Button
            variant="outlined"
            size="small"
            onClick={limparFiltros}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
          >
            Limpar filtros
          </Button>
        </>
      }
      paginacao={
        linhasFiltradas.length > 0 && (
          <Pagination
            paginationKey="aba-contribuicoes"
            data={linhasFiltradas}
            filteredData={linhasPagina}
            setFilteredData={setLinhasPagina}
          />
        )
      }
    >
      {linhasFiltradas.length === 0 ? (
        <p className={styles.emptyMsg}>Nenhum lançamento encontrado para esta apuração.</p>
      ) : (
        <div style={{ margin: "8px 0 0", overflow: "auto" }}>
        <table className={tableStyles.accordion_table}>
          <thead>
            <tr>
              <th scope="col" className={`${tableStyles.stickyKeyHeader} ${tableStyles.stickyKeyCol1}`}>CNPJ</th>
              <th scope="col">Bloco / registro</th>
              <th scope="col">Descrição</th>
              <th scope="col" style={NUM_TH}>Lançamentos</th>
              <th scope="col" style={NUM_TH}>Débito (R$)</th>
              <th scope="col" style={NUM_TH}>Crédito (R$)</th>
              <th scope="col" style={NUM_TH}>Saldo (R$)</th>
              <th scope="col">Validação</th>
              <th scope="col" style={{ textAlign: "center" }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {linhasPagina.map((linha) => (
              <tr key={linha.blocoRegistro}>
                <td className={`${tableStyles.stickyKeyCell} ${tableStyles.stickyKeyCol1}`}>{formatarCnpj(cnpj)}</td>
                <td>{linha.blocoRegistro}</td>
                <td>{linha.descricao || "—"}</td>
                <td style={NUM_TD}>{formatarQuantidadeLancamentos(linha.qtdLancamentos)}</td>
                <td style={NUM_TD}>{linha.debito == null ? "—" : formatarMoedaBRL(linha.debito)}</td>
                <td style={NUM_TD}>{linha.credito == null ? "—" : formatarMoedaBRL(linha.credito)}</td>
                <td style={NUM_TD}>{linha.saldo == null ? "—" : formatarMoedaBRL(linha.saldo)}</td>
                <td>
                  <BadgeStatus
                    tom={linha.validacao === "OK" ? "ok" : linha.validacao === "Pendente" ? "neutro" : "alerta"}
                  >
                    {linha.validacao}
                  </BadgeStatus>
                </td>
                <td style={{ textAlign: "center" }}>
                  {/* TXM-600 — drill-down por bloco (todas as competências da apuração), no
                      leiaute de 4 colunas da tela genérica do protótipo
                      (apuracao_lancamentos.html?tipo=sped-contribuicoes&conta={bloco}). */}
                  <button
                    type="button"
                    className={`${styles.btnHeader} ${styles.btnHeaderPrimary}`}
                    onClick={() =>
                      navigate(
                        `/relatorios/apuracoes/${apuracaoId}/detalhe/icms/lancamentos-contribuicoes?bloco=${linha.blocoRegistro}`
                      )
                    }
                  >
                    Detalhes
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </ApuracaoCardShell>

    <Snackbar
      open={infoSnack.open}
      autoHideDuration={4000}
      onClose={() => setInfoSnack((s) => ({ ...s, open: false }))}
    >
      <Alert severity={infoSnack.severity}>{infoSnack.msg}</Alert>
    </Snackbar>
    </>
  );
}
